import React, { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit, ChevronRight } from 'lucide-react';
import api from '../api/client';
import toast from 'react-hot-toast';
import Modal from '../components/ui/Modal';
import FormField, { Input, Select, SubmitBtn } from '../components/ui/FormField';

export default function AccountingAccountsPage() {
  const qc = useQueryClient();
  const [showNew, setShowNew] = useState(false);
  const [editAcc, setEditAcc] = useState<any>(null);
  const [form, setForm] = useState({ code:'', name:'', parent_code:'' });
  const [saving, setSaving] = useState(false);

  const { data } = useQuery({
    queryKey: ['accounts-all'],
    queryFn: () => api.get('/acquisitions/accounts'),
  } as any);

  const accounts = (data as any)?.data?.accounts || [];
  const parents = accounts.filter((a: any) => !a.parent_code);
  const set = (k: string) => (e: any) => setForm(f => ({...f, [k]: e.target.value}));

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.code || !form.name) return toast.error('კოდი და სახელი სავალდებულოა');
    setSaving(true);
    try {
      if (editAcc) {
        await api.put(`/acquisitions/accounts/${editAcc.id}`, form);
        toast.success('განახლდა!');
      } else {
        await api.post('/acquisitions/accounts', form);
        toast.success('ანგარიში დაემატა!');
      }
      qc.invalidateQueries({ queryKey: ['accounts-all'] });
      qc.invalidateQueries({ queryKey: ['accounts'] });
      setShowNew(false); setEditAcc(null);
      setForm({ code:'', name:'', parent_code:'' });
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'შეცდომა');
    } finally { setSaving(false); }
  };

  const openEdit = (a: any) => {
    setForm({ code: a.code, name: a.name, parent_code: a.parent_code||'' });
    setEditAcc(a);
  };

  return (
    <div style={{padding:24,fontFamily:'system-ui,sans-serif',maxWidth:700}}>
      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:24}}>
        <div>
          <h1 style={{fontSize:20,fontWeight:600,color:'#f1f5f9',margin:0}}>საბუღალტრო ანგარიშები</h1>
          <p style={{fontSize:12,color:'#475569',marginTop:4}}>ბიბლიოთეკური ფონდის ანგარიშთა გეგმა</p>
        </div>
        <button onClick={() => { setForm({ code:'', name:'', parent_code:'' }); setEditAcc(null); setShowNew(true); }}
          style={{display:'flex',alignItems:'center',gap:6,padding:'8px 16px',background:'#1d4ed8',border:'none',borderRadius:8,color:'white',fontSize:12,fontWeight:500,cursor:'pointer'}}>
          <Plus size={15}/> ახალი ანგარიში
        </button>
      </div>

      <div style={{background:'#0d1424',border:'1px solid #1e2d45',borderRadius:12,overflow:'hidden'}}>
        {parents.map((parent: any) => {
          const children = accounts.filter((a: any) => a.parent_code === parent.code);
          return (
            <div key={parent.id}>
              <div style={{display:'flex',alignItems:'center',gap:12,padding:'12px 16px',borderBottom:'1px solid #1e2d45',background:'#111827'}}>
                <div style={{width:36,height:36,borderRadius:8,background:'#0f2044',border:'1px solid #1e3a6e',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
                  <span style={{fontSize:11,fontFamily:'monospace',color:'#60a5fa',fontWeight:700}}>{parent.code}</span>
                </div>
                <div style={{flex:1}}>
                  <div style={{fontSize:14,fontWeight:600,color:'#f1f5f9'}}>{parent.name}</div>
                  <div style={{fontSize:11,color:'#475569',marginTop:1}}>{children.length} ქვეანგარიში</div>
                </div>
                <div style={{display:'flex',gap:6}}>
                  <button onClick={() => { setForm({ code:'', name:'', parent_code: parent.code }); setEditAcc(null); setShowNew(true); }}
                    style={{display:'flex',alignItems:'center',gap:4,padding:'5px 10px',background:'#0f2044',border:'1px solid #1e3a6e',borderRadius:6,fontSize:11,color:'#60a5fa',cursor:'pointer'}}>
                    <Plus size={11}/> ქვეანგარიში
                  </button>
                  <button onClick={() => openEdit(parent)}
                    style={{padding:'5px 8px',background:'#111827',border:'1px solid #1e2d45',borderRadius:6,color:'#64748b',cursor:'pointer'}}>
                    <Edit size={12}/>
                  </button>
                </div>
              </div>

              {children.map((child: any) => (
                <div key={child.id} style={{display:'flex',alignItems:'center',gap:12,padding:'10px 16px 10px 32px',borderBottom:'1px solid #0f1826'}}>
                  <ChevronRight size={12} style={{color:'#334155',flexShrink:0}} />
                  <div style={{width:60,padding:'3px 8px',borderRadius:6,background:'#080c14',border:'1px solid #1e2d45',textAlign:'center'}}>
                    <span style={{fontSize:10,fontFamily:'monospace',color:'#94a3b8'}}>{child.code}</span>
                  </div>
                  <span style={{flex:1,fontSize:13,color:'#cbd5e1'}}>{child.name}</span>
                  <button onClick={() => openEdit(child)}
                    style={{padding:'4px 8px',background:'#111827',border:'1px solid #1e2d45',borderRadius:6,color:'#64748b',cursor:'pointer'}}>
                    <Edit size={12}/>
                  </button>
                </div>
              ))}

              {children.length === 0 && (
                <div style={{padding:'8px 32px',fontSize:12,color:'#334155',fontStyle:'italic',borderBottom:'1px solid #0f1826'}}>
                  ქვეანგარიში არ არის
                </div>
              )}
            </div>
          );
        })}

        {parents.length === 0 && (
          <div style={{padding:32,textAlign:'center',color:'#334155',fontSize:13}}>
            ანგარიში არ არის — დაამატე პირველი!
          </div>
        )}
      </div>

      <Modal open={showNew || !!editAcc} onClose={() => { setShowNew(false); setEditAcc(null); }}
        title={editAcc ? 'ანგარიშის რედაქტირება' : form.parent_code ? `ქვეანგარიში — ${form.parent_code}` : 'ახალი ანგარიში'} width={440}>
        <form onSubmit={handleSubmit}>
          {!editAcc && (
            <FormField label="მთავარი ანგარიში">
              <Select value={form.parent_code} onChange={set('parent_code')}>
                <option value="">— მთავარი ანგარიში (root) —</option>
                {parents.map((p: any) => (
                  <option key={p.code} value={p.code}>{p.code} — {p.name}</option>
                ))}
              </Select>
            </FormField>
          )}
          <FormField label="კოდი" required>
            <Input value={form.code} onChange={set('code')} placeholder={form.parent_code ? `${form.parent_code}-01` : '0141'} />
          </FormField>
          <FormField label="დასახელება" required>
            <Input value={form.name} onChange={set('name')} placeholder="ანგარიშის სახელი" />
          </FormField>
          <SubmitBtn loading={saving} label={editAcc ? 'შენახვა' : 'დამატება'} />
        </form>
      </Modal>
    </div>
  );
}
